<template>
  <div class="card search-card">
    <div class="sc-head">
      <h3>Sök i rekorden</h3>
      <p class="hint-text">Välj övning och fråga antingen "hur många reps på X kg?" eller "hur tungt på N reps?". Finns ingen exakt träff visas närmaste under och över.</p>
    </div>

    <div class="sc-form">
      <label class="field grow">
        <span>Övning</span>
        <select v-model.number="exerciseTypeId">
          <option :value="null" disabled>Välj övning…</option>
          <optgroup v-if="favorites.length" label="★ Favoriter">
            <option v-for="e in favorites" :key="e.exerciseTypeId" :value="e.exerciseTypeId">{{ e.exerciseTypeName }}</option>
          </optgroup>
          <optgroup :label="favorites.length ? 'Övriga' : 'Övningar'">
            <option v-for="e in others" :key="e.exerciseTypeId" :value="e.exerciseTypeId">{{ e.exerciseTypeName }}</option>
          </optgroup>
        </select>
      </label>
      <div class="field">
        <span>Fråga</span>
        <div class="seg">
          <button :class="{ on: by === 'weight' }" @click="by = 'weight'">Vikt → reps</button>
          <button :class="{ on: by === 'reps' }" @click="by = 'reps'">Reps → vikt</button>
        </div>
      </div>
      <label class="field num">
        <span>{{ by === 'weight' ? 'Vikt (kg)' : 'Antal reps' }}</span>
        <input v-model="value" type="number" inputmode="decimal" :step="by === 'weight' ? 0.5 : 1" :min="by === 'weight' ? 0 : 1" :placeholder="by === 'weight' ? 't.ex. 100' : 't.ex. 5'" @keyup.enter="search" />
      </label>
      <button class="btn-primary" :disabled="!canSearch || loading" @click="search">{{ loading ? 'Söker…' : 'Sök' }}</button>
    </div>

    <p v-if="error" class="error">{{ error }}</p>

    <div v-if="result" class="sc-result">
      <div class="sc-title">
        {{ result.exerciseTypeName }} ·
        <template v-if="result.queryWeight != null">{{ fmtKg(result.queryWeight) }} kg</template>
        <template v-else>{{ result.queryReps }} rep{{ result.queryReps > 1 ? 's' : '' }}</template>
        <span class="mode-tag">{{ modeLabel }}</span>
      </div>

      <div v-if="!result.byWeight.length" class="empty-state">Inga giltiga lyft på den här övningen i valt läge.</div>

      <template v-else>
        <!-- Exakt träff -->
        <button v-if="result.exact" class="hit exact" @click="$emit('open-lift', hitContext(result.exact))">
          <span class="hit-label">🎯 Exakt träff</span>
          <span class="hit-main">{{ hitMain(result.exact) }}</span>
          <span class="hit-meta">{{ hitMeta(result.exact) }}</span>
        </button>

        <!-- Närmast under / över -->
        <div v-else class="near">
          <p class="near-note">Inget lyft på exakt {{ result.queryWeight != null ? fmtKg(result.queryWeight) + ' kg' : result.queryReps + ' reps' }}. Närmast:</p>
          <div class="near-grid">
            <button v-if="result.below" class="hit below" @click="$emit('open-lift', hitContext(result.below))">
              <span class="hit-label">▼ Under</span>
              <span class="hit-main">{{ hitMain(result.below) }}</span>
              <span class="hit-meta">{{ hitMeta(result.below) }}</span>
            </button>
            <div v-else class="hit none"><span class="hit-label">▼ Under</span><span class="hit-main muted">inget lättare</span></div>
            <button v-if="result.above" class="hit above" @click="$emit('open-lift', hitContext(result.above))">
              <span class="hit-label">▲ Över</span>
              <span class="hit-main">{{ hitMain(result.above) }}</span>
              <span class="hit-meta">{{ hitMeta(result.above) }}</span>
            </button>
            <div v-else class="hit none"><span class="hit-label">▲ Över</span><span class="hit-main muted">inget tyngre</span></div>
          </div>
        </div>

        <p v-if="estimateText" class="estimate">📐 {{ estimateText }}</p>

        <details class="profile">
          <summary>Hela profilen ({{ profileRows.length }} {{ by === 'weight' ? 'vikter' : 'repsnivåer' }})</summary>
          <div class="profile-grid">
            <button v-for="h in profileRows" :key="by + h.liftId + h.weight + h.reps" class="prow" :class="{ hi: isHighlighted(h) }" @click="$emit('open-lift', hitContext(h))">
              <span class="p-key">{{ by === 'weight' ? fmtKg(h.weight) + ' kg' : h.reps + ' reps' }}</span>
              <span class="p-val">{{ by === 'weight' ? h.reps + ' reps' : fmtKg(h.weight) + ' kg' }}</span>
              <span class="p-date">{{ fmtDate(h.date) }}<span v-if="h.status === 'competition'"> 🎖️</span></span>
            </button>
          </div>
        </details>
      </template>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { gymAuthState, refreshIfNeeded } from '../../services/gymTrackerAuth.js';
import api from '../../services/gymTrackerApi.js';

const MODE_LABELS = { all: 'alla giltiga', competition: 'tävling', training: 'träning' };

/*
 * PB-sökning. `exercises` = PB-listan (övningar med lyft, favoriter först); `mode`
 * följer PB-flikens växel. Emits open-lift(context) så föräldern kan öppna
 * LiftStatusSheet för träffen.
 */
export default {
  name: 'PbSearchCard',
  props: {
    exercises: { type: Array, default: () => [] },
    mode: { type: String, default: 'all' },
  },
  emits: ['open-lift'],
  setup(props, { emit }) {
    const exerciseTypeId = ref(null);
    const by = ref(localStorage.getItem('gymtracker.pbSearchBy') || 'weight');
    const value = ref('');
    const loading = ref(false);
    const error = ref('');
    const result = ref(null);

    const favorites = computed(() => props.exercises.filter(e => e.isFavorite));
    const others = computed(() => props.exercises.filter(e => !e.isFavorite));
    const canSearch = computed(() => exerciseTypeId.value != null && value.value !== '' && Number(value.value) > 0);
    const modeLabel = computed(() => MODE_LABELS[result.value?.mode] || result.value?.mode);

    async function withToken(fn) { await refreshIfNeeded(); return fn(gymAuthState.token); }

    async function search() {
      if (!canSearch.value) return;
      loading.value = true; error.value = '';
      try { localStorage.setItem('gymtracker.pbSearchBy', by.value); } catch { /* ignore */ }
      try {
        const params = { exerciseTypeId: exerciseTypeId.value, mode: props.mode };
        if (by.value === 'weight') params.weight = Number(value.value); else params.reps = Math.round(Number(value.value));
        const d = await withToken(t => api.getPbLookup(params, t));
        result.value = d.pbLookup;
      } catch (e) { error.value = e.message || String(e); }
      finally { loading.value = false; }
    }
    // Byter man läge/övning/fråga med ett resultat uppe: sök om automatiskt.
    watch(() => [props.mode, by.value, exerciseTypeId.value], () => { if (result.value && canSearch.value) search(); });

    const profileRows = computed(() => result.value ? (by.value === 'weight' ? result.value.byWeight : result.value.byReps) : []);
    const estimateText = computed(() => {
      const r = result.value;
      if (!r?.bestEstimated1Rm) return '';
      if (r.queryWeight != null && r.estimatedReps != null) {
        return `Uppskattat via bästa est. 1RM (${fmtKg(r.bestEstimated1Rm)} kg): ~${fmtKg(r.estimatedReps)} reps på ${fmtKg(r.queryWeight)} kg.`;
      }
      if (r.queryReps != null && r.estimatedWeight != null) {
        return `Uppskattat via bästa est. 1RM (${fmtKg(r.bestEstimated1Rm)} kg): ~${fmtKg(r.estimatedWeight)} kg på ${r.queryReps} reps.`;
      }
      return '';
    });

    const fmtKg = v => Number(v).toLocaleString('sv-SE', { maximumFractionDigits: 1 });
    const fmtDate = iso => new Date(iso).toLocaleDateString('sv-SE', { dateStyle: 'medium' });
    const hitMain = h => (by.value === 'weight' ? `${h.reps} reps på ${fmtKg(h.weight)} kg` : `${fmtKg(h.weight)} kg × ${h.reps}`);
    const hitMeta = h => [fmtDate(h.date), h.sessionName ? `pass "${h.sessionName}"` : 'löst lyft', h.count > 1 ? `${h.count} lyft i gruppen` : '', h.status === 'competition' ? '🎖️ tävling' : ''].filter(Boolean).join(' · ');
    const hitContext = h => ({ exerciseTypeId: result.value.exerciseTypeId, exerciseTypeName: result.value.exerciseTypeName, reps: h.reps, liftId: h.liftId });
    const isHighlighted = h => [result.value?.exact, result.value?.below, result.value?.above].some(x => x && x.liftId === h.liftId);

    return { exerciseTypeId, by, value, loading, error, result, favorites, others, canSearch, modeLabel, search, profileRows, estimateText, fmtKg, fmtDate, hitMain, hitMeta, hitContext, isHighlighted, emit };
  },
};
</script>

<style scoped>
.card { background: var(--gym-surface); border: 1px solid var(--gym-border); border-radius: var(--gym-radius-lg); padding: 1.25rem; box-shadow: var(--gym-shadow-sm); display: flex; flex-direction: column; gap: 0.9rem; }
.card h3 { margin: 0 0 0.35rem; font-size: 1.02rem; color: var(--gym-text); font-weight: 700; }
.hint-text { color: var(--gym-text-muted); font-size: 0.85rem; margin: 0; line-height: 1.55; }
.error { margin: 0; color: var(--gym-danger); font-size: 0.88rem; font-weight: 600; }
.empty-state { color: var(--gym-text-muted); font-size: 0.9rem; }
.muted { color: var(--gym-text-faint) !important; font-weight: 500 !important; }

.sc-form { display: flex; gap: 0.7rem; align-items: flex-end; flex-wrap: wrap; }
.field { display: flex; flex-direction: column; gap: 0.3rem; font-size: 0.78rem; font-weight: 700; color: var(--gym-text-muted); text-transform: uppercase; letter-spacing: 0.03em; }
.field.grow { flex: 1 1 220px; min-width: 200px; }
.field.num { width: 130px; }
.field select, .field input { padding: 0.55rem 0.7rem; border: 1px solid var(--gym-border); border-radius: var(--gym-radius-sm); background: var(--gym-surface-2); color: var(--gym-text); font-size: 0.92rem; text-transform: none; letter-spacing: 0; font-weight: 500; width: 100%; box-sizing: border-box; }
.field select:focus, .field input:focus { outline: none; border-color: var(--gym-accent); }
.seg { display: inline-flex; border: 1px solid var(--gym-border); border-radius: var(--gym-radius-sm); overflow: hidden; }
.seg button { border: none; border-right: 1px solid var(--gym-border); background: var(--gym-surface-2); color: var(--gym-text-muted); padding: 0.55rem 0.8rem; font-size: 0.84rem; font-weight: 700; cursor: pointer; white-space: nowrap; }
.seg button:last-child { border-right: none; }
.seg button.on { background: var(--gym-accent); color: var(--gym-on-accent); }
.btn-primary { background: var(--gym-accent); color: var(--gym-on-accent); border: none; padding: 0.6rem 1.3rem; border-radius: var(--gym-radius-sm); font-size: 0.92rem; font-weight: 700; cursor: pointer; }
.btn-primary:hover:not(:disabled) { background: var(--gym-accent-hover); }
.btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }

.sc-result { display: flex; flex-direction: column; gap: 0.7rem; border-top: 1px solid var(--gym-border); padding-top: 0.8rem; }
.sc-title { font-weight: 700; color: var(--gym-text); font-size: 0.95rem; display: flex; gap: 0.4rem; align-items: center; flex-wrap: wrap; }
.mode-tag { font-size: 0.7rem; font-weight: 700; padding: 0.12rem 0.5rem; border-radius: var(--gym-radius-pill); background: var(--gym-surface-3); color: var(--gym-text-muted); text-transform: uppercase; letter-spacing: 0.03em; }

.hit { display: flex; flex-direction: column; gap: 0.2rem; align-items: flex-start; text-align: left; border: 1px solid var(--gym-border); border-left: 4px solid var(--gym-accent); border-radius: var(--gym-radius); background: var(--gym-surface-2); padding: 0.75rem 1rem; cursor: pointer; color: var(--gym-text); transition: border-color var(--gym-dur) var(--gym-ease), transform var(--gym-dur) var(--gym-ease); }
.hit:hover { border-color: var(--gym-accent-border); }
.hit:active { transform: scale(0.99); }
.hit.exact { border-left-color: var(--gym-success); background: var(--gym-success-soft); }
.hit.below { border-left-color: var(--gym-warning); }
.hit.above { border-left-color: var(--gym-accent); }
.hit.none { cursor: default; border-left-color: var(--gym-border-strong); opacity: 0.7; }
.hit-label { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--gym-text-muted); }
.hit-main { font-size: 1.35rem; font-weight: 800; color: var(--gym-accent); line-height: 1.1; }
.hit.exact .hit-main { color: var(--gym-success); }
.hit-meta { font-size: 0.8rem; color: var(--gym-text-muted); }
.near { display: flex; flex-direction: column; gap: 0.5rem; }
.near-note { margin: 0; font-size: 0.85rem; color: var(--gym-text-muted); }
.near-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.6rem; }
.estimate { margin: 0; font-size: 0.82rem; color: var(--gym-text-muted); }

.profile summary { cursor: pointer; font-size: 0.85rem; font-weight: 700; color: var(--gym-text-muted); }
.profile-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 0.4rem; margin-top: 0.6rem; }
.prow { display: flex; flex-direction: column; align-items: flex-start; gap: 0.05rem; border: 1px solid var(--gym-border); border-radius: var(--gym-radius-sm); background: var(--gym-surface-2); padding: 0.45rem 0.6rem; cursor: pointer; color: var(--gym-text); text-align: left; }
.prow.hi { border-color: var(--gym-accent-border); background: var(--gym-accent-softer); }
.p-key { font-size: 0.78rem; font-weight: 700; color: var(--gym-text-muted); }
.p-val { font-size: 1rem; font-weight: 800; color: var(--gym-accent); }
.p-date { font-size: 0.7rem; color: var(--gym-text-faint); }

@media (max-width: 600px) {
  .near-grid { grid-template-columns: 1fr; }
  .field.num { width: 100%; }
  .btn-primary { width: 100%; }
  .seg { width: 100%; }
  .seg button { flex: 1; }
}
</style>
